import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Sentry, captureException } from 'src/utils/sentry';

import ErrorPage from '../pages/Error';

const CHUNK_LOAD_RELOAD_KEY = 'excom.chunkLoadReloaded';
const CHUNK_LOAD_ERROR_MESSAGES = [
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  'ChunkLoadError',
];

interface Props {
  fallback?: ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isRecovering: boolean;
  errorMessage: string;
}

function isChunkLoadError(error: Error): boolean {
  const message = error.message || error.toString();
  return CHUNK_LOAD_ERROR_MESSAGES.some((m) => message.includes(m));
}

function reloadKey(pathname: string): string {
  return `${CHUNK_LOAD_RELOAD_KEY}:${pathname}`;
}

function hasAlreadyReloadedForChunkLoad(pathname: string): boolean {
  try {
    return window.sessionStorage.getItem(reloadKey(pathname)) === 'true';
  } catch (_error) {
    return false;
  }
}

function markChunkLoadReloaded(pathname: string): void {
  try {
    window.sessionStorage.setItem(reloadKey(pathname), 'true');
  } catch (_error) {
    // sessionStorage unavailable (private mode, quota); fall through to error UI.
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isRecovering: false,
      errorMessage: '',
    };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorMessage = error.message || error.toString();
    const isRecoverableChunkError =
      isChunkLoadError(error) &&
      !hasAlreadyReloadedForChunkLoad(window.location.pathname);

    return {
      hasError: true,
      isRecovering: isRecoverableChunkError,
      errorMessage,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.state.isRecovering) {
      Sentry.captureMessage(
        'ErrorBoundary recovered from chunk-load error via reload',
        {
          level: 'warning',
          tags: { chunkLoadRecovery: 'attempted' },
          extra: {
            pathname: window.location.pathname,
            originalMessage: error.message,
          },
        }
      );
      markChunkLoadReloaded(window.location.pathname);
      window.location.reload();
      return;
    }

    captureException(error, errorInfo);

    // eslint-disable-next-line no-console
    console.error(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.isRecovering) {
      return null;
    }
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorPage message={this.state.errorMessage} />;
    }
    return this.props.children;
  }
}
